import { clearAuthToken, getAuthToken } from "@/lib/authToken";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api/v1";
const API_ORIGIN = API_BASE.replace(/\/api\/v\d+$/, "");

/** Full API base URL — for plain links to API-served files (e.g. the NDA PDF). */
export const API_BASE_URL = API_BASE;

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = "ApiError";
    this.status = status;
  }
}

/**
 * Clear the stored session and redirect to sign-in. Fired when the API rejects
 * an authenticated request with 401 (e.g. the 1-hour inactivity timeout). Guarded
 * so a burst of failing requests only redirects once.
 */
let sessionExpiredHandled = false;
function handleSessionExpired(): void {
  if (sessionExpiredHandled) return;
  sessionExpiredHandled = true;
  clearAuthToken();
  try {
    sessionStorage.setItem("session_expired", "1");
  } catch { /* ignore */ }
  if (!window.location.pathname.startsWith("/sign-in")) {
    window.location.assign("/sign-in");
  }
}

/** Resolve a relative upload path (e.g. /uploads/avatars/x.jpg) to a full URL */
export function assetUrl(path: string | null | undefined): string | null {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("data:")) return path;
  return `${API_ORIGIN}${path}`;
}

async function request<T>(url: string, options?: RequestInit): Promise<T> {
  const token = getAuthToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options?.headers,
  };

  const res = await fetch(`${API_BASE}${url}`, { ...options, headers });

  // A 401 on an authenticated request means the session is no longer valid
  // (expired after inactivity, or the account was invalidated server-side).
  // Drop the token and bounce to sign-in so stale sessions can't linger.
  if (res.status === 401 && token) {
    handleSessionExpired();
    throw new ApiError(401, "Session expired. Please log in again.");
  }

  // A fresh successful call means the (re-)authenticated session is valid again,
  // so re-arm the one-shot expiry guard for any future timeout.
  if (res.ok) sessionExpiredHandled = false;

  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const detail = body.detail;
    const msg =
      typeof detail === "string"
        ? detail
        : typeof detail === "object" && detail !== null && typeof detail.message === "string"
        ? detail.message
        : Array.isArray(detail) && typeof detail[0]?.msg === "string"
        ? // FastAPI/Pydantic 422: name the offending field instead of a bare status.
          `${detail[0].loc?.slice(1).join(".") || "Request"}: ${detail[0].msg}`
        : `Request failed (${res.status})`;
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json();
}

/** Fetch a binary file (e.g. a PDF) with the auth header — a plain <a href> to
 * an authenticated endpoint can't send the bearer token. Returns a Blob. */
async function requestBlob(url: string): Promise<Blob> {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${url}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401 && token) {
    handleSessionExpired();
    throw new ApiError(401, "Session expired. Please log in again.");
  }
  if (!res.ok) throw new ApiError(res.status, `Request failed (${res.status})`);
  return res.blob();
}

/**
 * POST a JSON body, read back a Server-Sent Events stream, and call
 * `onEvent` with each parsed `data: {...}` payload as it arrives — not
 * buffered until the response completes, which is the entire point of using
 * this over a plain `api.post`. Same auth-header/401 handling as `request()`
 * above, since `fetch`'s streaming body reader doesn't go through it.
 *
 * SSE framing is `data: <json>\n\n` per event; a partial event can arrive
 * split across two chunks, so incomplete text is buffered and only
 * complete `\n\n`-terminated chunks are parsed.
 */
async function requestStream(
  url: string,
  data: unknown,
  onEvent: (event: Record<string, unknown>) => void,
): Promise<void> {
  const token = getAuthToken();
  const res = await fetch(`${API_BASE}${url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (res.status === 401 && token) {
    handleSessionExpired();
    throw new ApiError(401, "Session expired. Please log in again.");
  }
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    throw new ApiError(res.status, typeof body.detail === "string" ? body.detail : `Request failed (${res.status})`);
  }
  if (res.ok) sessionExpiredHandled = false;

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const chunks = buffer.split("\n\n");
    buffer = chunks.pop() ?? "";
    for (const chunk of chunks) {
      const line = chunk.trim();
      if (!line.startsWith("data:")) continue;
      try {
        onEvent(JSON.parse(line.slice(5).trim()));
      } catch {
        /* malformed chunk — skip rather than abort the whole stream */
      }
    }
  }
}

/**
 * The subset of a client that page components may depend on.
 *
 * Both `api` (portal token) and the super admin console's `superAdminApi`
 * satisfy this, which is what lets one page component be mounted in either
 * console. A page must never import `api` directly if it can also be rendered
 * for the super admin: that would send the portal token and, on 401, throw the
 * super admin out to the portal sign-in page.
 */
export interface ApiClient {
  get: <T>(url: string) => Promise<T>;
  post: <T>(url: string, data: unknown) => Promise<T>;
  put: <T>(url: string, data: unknown) => Promise<T>;
  patch: <T>(url: string, data: unknown) => Promise<T>;
  delete: <T>(url: string) => Promise<T>;
  upload: <T>(url: string, file: File, fieldName?: string, fields?: Record<string, string>) => Promise<T>;
}

export const api = {
  get: <T>(url: string) => request<T>(url),
  blob: (url: string) => requestBlob(url),
  postStream: (url: string, data: unknown, onEvent: (event: Record<string, unknown>) => void) =>
    requestStream(url, data, onEvent),
  post: <T>(url: string, data: unknown) => request<T>(url, { method: "POST", body: JSON.stringify(data) }),
  put: <T>(url: string, data: unknown) => request<T>(url, { method: "PUT", body: JSON.stringify(data) }),
  patch: <T>(url: string, data: unknown) => request<T>(url, { method: "PATCH", body: JSON.stringify(data) }),
  delete: <T>(url: string) => request<T>(url, { method: "DELETE" }),
  upload: <T>(url: string, file: File, fieldName = "file", fields?: Record<string, string>) => {
    const token = getAuthToken();
    const form = new FormData();
    form.append(fieldName, file);
    if (fields) for (const [k, v] of Object.entries(fields)) form.append(k, v);
    return fetch(`${API_BASE}${url}`, {
      method: "POST",
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    }).then(async (res) => {
      if (res.status === 401 && token) {
        handleSessionExpired();
        throw new ApiError(401, "Session expired. Please log in again.");
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.detail || `Upload failed (${res.status})`);
      }
      return res.json() as Promise<T>;
    });
  },
};
